import { createAsyncThunk } from "@reduxjs/toolkit";

import * as api from "@/lib/api";
import { clearSessionPersistence } from "@/lib/authPersistence";
import { writeBranchCookie } from "@/lib/branch";
import { activeTenantId, setActiveTenantId } from "@/lib/config";
import type { Customer, Staff } from "@/types";
import { trackEvent } from "@/utils/analytics";
import {
  resetAuthState,
  setAuthState,
  setCurrentCustomer,
  setFetchingCustomerInfo,
  setStaffState,
  staffUid,
} from "../slices/authSlice";
import { setSessionVoucherCodes } from "../slices/commonSlice";
import { resetHistory } from "../slices/historySlice";
import {
  resetOperationalRoles,
  setOperationalRoles,
  setOperationalRolesLoading,
} from "../slices/operationalRolesSlice";
import { setSnackbar } from "../slices/snackbarSlice";
import { persistor, type RootState } from "..";

/**
 * One tab's record of the branch we last tried to send a staff member to.
 * `sessionStorage`, not `localStorage`: the guard is about THIS navigation
 * sequence, and a new tab deserves a fresh attempt.
 */
const MOVE_ATTEMPT_KEY = "zenvix:staff-branch-move";

/**
 * Send a staff member to the branch they belong to — AT MOST ONCE PER TARGET.
 *
 * THE LOOP THIS GUARD EXISTS FOR.
 *
 * `window.location.assign("/?branch=X")` is a full reload, and it only takes
 * effect if the deployment can actually serve branch X. It cannot when X
 * belongs to a DIFFERENT BRAND: `lib/branch.ts` resolves the branch against
 * the brand this host serves, so a foreign `?branch=` is ignored and the host
 * branch wins (`source: "host"`).
 *
 * `fetchStaffDetails` seeds its lookup from the PERSISTED `auth.staff.tenant_id`,
 * so a staff record left over from another brand keeps resolving to that brand.
 * On ordring.zenvix.cloud (butt-karahi-main) a persisted Legend Bites staff
 * record therefore produced: bootstrap -> assign "/?branch=legend-bite-alipurbranch"
 * -> server ignores it and serves butt-karahi-main -> bootstrap -> assign again,
 * forever. That endless reload is the "flickering" the operator reported, and
 * the URL they quoted is the one the loop kept navigating to.
 *
 * The rule is simply: a move that did not take effect will not take effect by
 * repeating it. So we try once per target per tab, and if we come back still
 * not on that branch we stay put and say so, rather than reloading forever.
 */
const moveToStaffTenant = async (staff: Staff, currentTenantId?: string) => {
  const assignedTenantId = staff.tenant_id;
  if (typeof window === "undefined") return false;

  // Already there: the move worked (or was never needed). Clear the marker so a
  // later, legitimate switch away and back is not mistaken for a failed retry.
  if (!assignedTenantId || assignedTenantId === currentTenantId) {
    try { window.sessionStorage.removeItem(MOVE_ATTEMPT_KEY); } catch { /* private mode */ }
    return false;
  }

  let lastAttempt: string | null = null;
  try { lastAttempt = window.sessionStorage.getItem(MOVE_ATTEMPT_KEY); } catch { lastAttempt = null; }
  if (lastAttempt === assignedTenantId) {
    // We already reloaded for this branch and we are still not on it, so this
    // deployment cannot serve it. Staying is the only non-looping option.
    // eslint-disable-next-line no-console
    console.warn(
      `[auth] this site serves "${currentTenantId ?? "unknown"}" and cannot switch to ` +
      `"${assignedTenantId}" — staying put instead of reloading again.`,
    );
    return false;
  }
  try { window.sessionStorage.setItem(MOVE_ATTEMPT_KEY, assignedTenantId); } catch { /* private mode */ }

  setActiveTenantId(assignedTenantId);
  writeBranchCookie(assignedTenantId);
  await persistor.flush();
  window.location.assign(`/?branch=${encodeURIComponent(assignedTenantId)}`);
  return true;
};

/**
 * The auth thunks, ported from the original's module **88745**. They live
 * outside the slice because they dispatch into `common` and `history`, and
 * importing those from the slice would close an import cycle.
 *
 * What is here and what is NOT is deliberate:
 *
 * - `setAuthLogIn` (`x` / `Y0`) and `setLogout` (`D` / `wt`) are the two the
 *   customer flow needs, and both are reproduced including the ordering that
 *   makes them observable — the analytics event fires only on a TRANSITION into
 *   the signed-in state, not on every re-login.
 * - `syncCustomerContextByPhone` (`O`) is reproduced only as far as this
 *   deployment can reach: it re-reads the customer and merges. Its second half
 *   — rebuilding `cart.delivery_details` from the customer's stored
 *   `address_details` and re-pricing it through the client-side delivery
 *   calculator — is unreachable, because `/web/auth/session` carries no
 *   `address_details` (nothing in this deployment writes one) and pricing is
 *   server-side here by an earlier settled decision. Flagged, not faked.
 * - `fetchStaffDetails` (`T`) and `switchStaffAuth` (`R` / `Mq`) are at the
 *   bottom of this file. See `recovery-source/SPEC_staff-login.md`.
 */

/**
 * `O` — re-read the signed-in customer and merge the fresh record over the
 * stored one.
 *
 * The merge direction matters and is the original's: server fields win, but
 * anything the visitor typed locally and has not sent anywhere (a name entered
 * on the customer form, say) survives, because it is not in the response at
 * all.
 */
export const syncCustomerContextByPhone = createAsyncThunk<
  Customer | null,
  string,
  { state: RootState }
>("auth-slice/syncCustomerContextByPhone", async (phone, { dispatch, getState }) => {
  const trimmed = phone?.trim() ?? "";
  if (!trimmed) return null;
  dispatch(setFetchingCustomerInfo(true));
  try {
    /* ---------------------------- the staff fork --------------------------- */
    //
    // §1.2 of the spec. The original gates on the Firebase custom claim
    // `is_staff` and only then confirms with
    // `GET /tenant-staff/{phone}/exists`; `:4001` mints no such claim, so the
    // `exists` call IS the gate here. It is the same question asked of the same
    // endpoint — one round trip earlier.
    //
    // On the staff branch the original returns `null` so the customer reducer
    // leaves `auth.customer` alone. Reproduced: a signed-in operator keeps
    // whatever customer identity the login gave them (the original keeps a
    // `{ uid, phone_number }` stub for the same reason), and `isStaff` is a
    // SEPARATE axis. That is what stops the two sessions bleeding into one
    // another in either direction.
    // A signed-in operator already in the store WINS over the phone number, and
    // this is not an optimisation: "Switch Staff" replaces the operator without
    // touching the customer identity, so re-resolving from the phone here would
    // silently switch them back on the next page load. `bootstrapStaffSession`
    // is what re-reads that record.
    if (staffUid(getState().auth.staff)) return null;

    const currentTenantId = activeTenantId() || getState().settings.data?._id;
    const staff = await api.resolveBrandStaff({ phone_number: trimmed }, currentTenantId || undefined);
    if (staff) {
      dispatch(setStaffState(staff));
      if (await moveToStaffTenant(staff, currentTenantId)) return null;
      if (!staff.is_disabled) await dispatch(loadOperationalRoles());
      return null;
    }

    /* -------------------------- the customer branch ------------------------ */
    //
    // `P(null)` + `b.dA` — the original clears the staff record and the role
    // matrix on EVERY customer sync, not only on logout. Without it, signing a
    // customer in on a browser that had a staff session leaves that session's
    // permissions answering questions for somebody else.
    if (getState().auth.isStaff) {
      dispatch(setStaffState(null));
      dispatch(resetOperationalRoles());
    }
    const fresh = await api.refreshCustomerSession(trimmed);
    if (fresh) {
      dispatch(setCurrentCustomer({ ...getState().auth.customer, ...fresh }));
    }
    return fresh;
  } finally {
    dispatch(setFetchingCustomerInfo(false));
  }
});

/**
 * `x` / `Y0` — the login write.
 *
 * The original reads `isLoggedIn` BEFORE setting it and fires
 * `UserAuthenticated` only when it was false, so a customer who re-verifies is
 * not counted twice. Reproduced.
 *
 * It does not re-read the customer: unlike the original — whose Firebase
 * callback hands it only a uid — our `/web/auth/verify-otp` response already
 * carries the whole record, including the wallet fields and `order_ids`, so a
 * follow-up `/web/auth/session` would be a strictly staler copy of what we
 * just received.
 */
export const setAuthLogIn = createAsyncThunk<
  void,
  Customer,
  { state: RootState }
>("auth-slice/setAuthLogIn", async (customer, { dispatch, getState }) => {
  const wasLoggedIn = getState().auth.isLoggedIn;
  dispatch(setAuthState({ customer, isLoggedIn: true }));
  if (!wasLoggedIn) {
    trackEvent("UserAuthenticated", {
      phone_number: customer.phone ?? customer.phone_number ?? "",
      name: customer.name ?? customer.display_name ?? "",
    });
  }
});

/**
 * `D` / `wt` — sign out.
 *
 * The original clears four things in this order: the Firebase session, the
 * cart's consumer block, the session voucher codes, the auth state, the API
 * auth headers and the order history. Three of those six have no counterpart
 * here — there is no Firebase session, no separate cart consumer block (this
 * tree's checkout identity IS `auth.customer`) and no per-request auth header
 * for customers — so what remains is the three that do.
 *
 * The history reset is the one that is easy to miss and visible when it is:
 * without it the recent-orders rail keeps rendering the previous customer's
 * orders until something else refetches.
 */
export const setLogout = createAsyncThunk<void, void>(
  "auth-slice/setLogout",
  async (_arg, { dispatch }) => {
    // Stands in for the original's `signOut()` on the Firebase session: it is
    // what ends the persistence choice, so the next login starts from the
    // checkbox's default rather than inheriting the last one.
    clearSessionPersistence();
    dispatch(setSessionVoucherCodes(null));
    dispatch(resetAuthState());
    // `b.dA` — the role matrix goes with the session. `resetAuthState` already
    // drops `staff`, but the matrix lives in its own slice and a stale one
    // would keep answering permission questions for nobody.
    dispatch(resetOperationalRoles());
    dispatch(resetHistory());
  },
);

/* -------------------------------------------------------------------------- */
/* the staff half                                                             */
/* -------------------------------------------------------------------------- */

/**
 * `5342.CG` — load the tenant's role matrix.
 *
 * The original sends the Firebase bearer and `x-api-key` with it; `:4001`
 * scopes by tenant and needs neither, so the headers argument has no
 * counterpart. It never rejects: `fetchOperationalRoles` answers `null` on a
 * failure and `null` reads as "no permissions", which is the safe direction.
 */
export const loadOperationalRoles = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("operational-roles/fetchOperationalRoles", async (_arg, { dispatch, getState }) => {
  const tenantId = activeTenantId() || getState().settings.data?._id;
  dispatch(setOperationalRolesLoading(true));
  const roles = await api.fetchOperationalRoles(tenantId || undefined);
  dispatch(setOperationalRoles(roles));
});

/**
 * `T` — `"auth-slice/fetchStaffDetails"`.
 *
 * Re-reads the operator on every boot so a role change, a platform change or a
 * disablement takes effect on the next page load rather than at the next
 * sign-in. `setStaffState` does the rest: a `null` payload and an
 * `is_disabled` payload both collapse to "not staff".
 *
 * **Not reproduced:** the original also dispatches
 * `updateCartData({ staff_details, consumer: <blank> })` here — it writes the
 * operator onto the cart and wipes the cart's own consumer block so the staff
 * member types the customer in. Neither half has a counterpart yet: this
 * tree's checkout identity IS `auth.customer` (wiping it would sign the
 * CUSTOMER out, not clear a separate field), and `POST /web/orders` writes
 * `staff_details: null` deliberately, because the POS's "Assign Server"
 * control is gated on it being null. Recorded in `RECOVERY_PLAN.md` rather
 * than half-wired.
 */
export const fetchStaffDetails = createAsyncThunk<
  Staff | null,
  string,
  { state: RootState }
>("auth-slice/fetchStaffDetails", async (uid, { dispatch, getState }) => {
  const currentTenantId = activeTenantId() || getState().settings.data?._id;
  const tenantId = getState().auth.staff?.tenant_id || currentTenantId;
  try {
    const staff = await api.fetchStaffDetails(uid, tenantId || undefined);
    dispatch(setStaffState(staff ?? null));
    if (staff && await moveToStaffTenant(staff, currentTenantId)) return staff;
    if (staff && !staff.is_disabled) await dispatch(loadOperationalRoles());
    return staff ?? null;
  } catch {
    // `T.rejected → { staff: null, isStaff: false }`, verbatim. A staff record
    // that cannot be read is not a staff record.
    dispatch(setStaffState(null));
    dispatch(resetOperationalRoles());
    return null;
  }
});

/**
 * `R` / `Mq` — `"auth/switchStaffAuth"`, the 6-digit access-code submit.
 *
 * The original posts the code, signs the returned custom token into Firebase
 * and lets the auth listener fetch the record back. There is no Firebase here,
 * so the `staff` on the same response is used directly — which is what its own
 * reducer does with it (`R.fulfilled → { staff: payload, isStaff: true }`).
 *
 * Both snackbars are verbatim, including their anchoring: success
 * `` `Welcome ${display_name}!` ``, failure `"Please try again!"`, both
 * `variant: "filled"` and anchored top-centre. The modal is closed by the
 * caller on a resolved promise, which is where the original's `closeModal()`
 * sits in the chain.
 *
 * `fetchingCustomerInfo` is what the access-code form's spinner reads — the
 * original shares that one flag between the customer lookup and this switch.
 */
export const switchStaffAuth = createAsyncThunk<
  Staff,
  string,
  { state: RootState }
>("auth/switchStaffAuth", async (code, { dispatch, getState, rejectWithValue }) => {
  const tenantId = activeTenantId() || getState().settings.data?._id;
  dispatch(setFetchingCustomerInfo(true));
  try {
    const staff = await api.resolveBrandStaff({ code }, tenantId || undefined);
    if (!staff) throw new Error("Staff not found for this brand");
    dispatch(setStaffState(staff));
    if (await moveToStaffTenant(staff, tenantId)) return staff;
    // The matrix has to land before anything asks a permission question, or
    // the Account menu paints once without its staff entries.
    await dispatch(loadOperationalRoles());
    dispatch(
      setSnackbar({
        message: `Welcome ${staff.display_name}!`,
        severity: "success",
        // The original passes `variant: "filled"` too; the single snackbar
        // host here already renders every alert filled, so the field would be
        // dead state.
        anchorOrigin: { vertical: "top", horizontal: "center" },
      }),
    );
    return staff;
  } catch (error) {
    dispatch(
      setSnackbar({
        message: "Please try again!",
        severity: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      }),
    );
    return rejectWithValue(error) as never;
  } finally {
    dispatch(setFetchingCustomerInfo(false));
  }
});

/**
 * The boot re-read, the storefront's stand-in for `onAuthStateChanged` on the
 * staff side. `auth` is persisted, so `staff` survives a reload on its own —
 * but the role matrix is deliberately NOT persisted, and a permission answered
 * from a restored matrix would outlive its revocation.
 */
export const bootstrapStaffSession = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("auth-slice/bootstrapStaffSession", async (_arg, { dispatch, getState }) => {
  const uid = staffUid(getState().auth.staff);
  if (!uid) return;
  await dispatch(fetchStaffDetails(uid));
});
